import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Play, CheckCircle2, ExternalLink, Clock, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import LeadFormModal from '../components/LeadFormModal';
import WistiaPlayer from '../components/WistiaPlayer';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getWebinarById, getWebinars, trackImpression, trackUnlockClick, trackWebinarUnlocked, hasSessionLead } from '../services/api';
import { getWebinarThumbnail } from '../utils/thumbnailHelper';

const WebinarDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [webinar, setWebinar] = useState(null);
  const [relatedWebinars, setRelatedWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLeadFormOpen, setIsLeadFormOpen] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [showProgramCTA, setShowProgramCTA] = useState(false);

  useEffect(() => {
    const fetchWebinar = async () => {
      try {
        setLoading(true);
        const response = await getWebinarById(id);
        
        if (response.success && response.webinar) {
          setWebinar(response.webinar);
          
          // Track impression
          trackImpression(id);
          
          // Check if user has session-scoped access (sessionStorage only)
          if (hasSessionLead()) {
            setHasAccess(true);
            setShowProgramCTA(true);
          } else {
            // Check if we should auto-open the lead form (from hero CTA)
            const shouldOpenForm = searchParams.get('open_form') === 'true';
            if (shouldOpenForm) {
              trackUnlockClick(id);
              setIsLeadFormOpen(true);
              // Remove the query param to prevent re-opening on refresh
              searchParams.delete('open_form');
              setSearchParams(searchParams, { replace: true });
            }
          }
          
          // Fetch related webinars
          fetchRelatedWebinars(response.webinar);
        }
      } catch (error) {
        console.error('Error fetching webinar:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchWebinar();
    }
  }, [id, searchParams, setSearchParams]);

  const fetchRelatedWebinars = async (currentWebinar) => {
    try {
      const response = await getWebinars();
      if (response.success && response.webinars) {
        // Filter related webinars by same category or similar tags
        const related = response.webinars
          .filter(w => w.webinar_id !== currentWebinar.webinar_id)
          .filter(w => {
            const sameCategory = w.category === currentWebinar.category;
            const hasSimilarTags = currentWebinar.tags && w.tags && 
              w.tags.some(tag => currentWebinar.tags.includes(tag));
            return sameCategory || hasSimilarTags;
          })
          .slice(0, 4);
        setRelatedWebinars(related);
      }
    } catch (error) {
      console.error('Error fetching related webinars:', error);
    }
  };

  const handleLeadFormSubmit = (response) => {
    console.log('Lead captured:', response);
    setHasAccess(true);
    setShowProgramCTA(true);

    // Fire webinar_unlocked once per session (only on form submission)
    if (webinar) {
      trackWebinarUnlocked(
        id,
        webinar.title,
        webinar.category,
        window.location.href
      );
    }
  };

  const handleWatchClick = () => {
    if (!hasAccess) {
      trackUnlockClick(id);
      setIsLeadFormOpen(true);
    }
  };
  
  const thumbnail = webinar ? getWebinarThumbnail(webinar) : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading webinar...</p>
        </div>
      </div>
    );
  }

  if (!webinar) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Webinar Not Found</h1>
          <Button onClick={() => navigate('/library')}>
            Back to Library
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO Meta Tags */}
      {webinar && (
        <Helmet>
          <title>{webinar.meta_title || webinar.title} | Great Learning</title>
          <meta name="description" content={webinar.meta_description || webinar.short_description || webinar.description} />
          <meta property="og:title" content={webinar.meta_title || webinar.title} />
          <meta property="og:description" content={webinar.meta_description || webinar.short_description} />
          <meta property="og:image" content={thumbnail} />
          <meta property="og:type" content="video.other" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={webinar.meta_title || webinar.title} />
          <meta name="twitter:description" content={webinar.meta_description || webinar.short_description} />
          <meta name="twitter:image" content={thumbnail} />
          <link rel="canonical" href={`${window.location.origin}/webinar/${webinar.webinar_id}`} />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "VideoObject",
              "name": webinar.title,
              "description": webinar.description,
              "thumbnailUrl": thumbnail,
              "uploadDate": webinar.published_date,
              "duration": `PT${webinar.video_duration}S`,
              "publisher": {
                "@type": "Organization",
                "name": "Great Learning",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://www.mygreatlearning.com/logo.png"
                }
              }
            })}
          </script>
        </Helmet>
      )}
      
      <Header />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/library')}
          className="mb-6 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Library
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {!hasAccess ? (
                <div className="relative aspect-video bg-gray-900 flex items-center justify-center group cursor-pointer"
                     onClick={handleWatchClick}>
                  <img
                    src={thumbnail}
                    alt={webinar.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=450&fit=crop';
                    }}
                  />
                  <div className="relative z-10 text-center">
                    <div className="bg-blue-600 rounded-full p-6 inline-flex mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Play className="h-12 w-12 text-white" fill="white" />
                    </div>
                    <p className="text-white text-lg font-medium">Watch Full Webinar</p>
                    <p className="text-gray-300 text-sm mt-2">Submit details to access</p>
                  </div>
                </div>
              ) : (
                <div className="aspect-video bg-gray-900">
                  <WistiaPlayer 
                    mediaId={webinar.wistia_media_id} 
                    webinarId={webinar.webinar_id}
                    webinarTitle={webinar.title}
                  />
                </div>
              )}
            </div>

            <div className="mt-6 bg-white rounded-lg shadow-sm p-6">
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                  {webinar.category}
                </Badge>
                <Badge variant="secondary">{webinar.level}</Badge>
                {webinar.webinar_type && (
                  <Badge variant="outline">{webinar.webinar_type}</Badge>
                )}
                {webinar.tags && webinar.tags.slice(0, 3).map((tag, idx) => (
                  <Badge key={idx} variant="outline">{tag}</Badge>
                ))}
              </div>

              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                {webinar.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-gray-600 mb-6">
                <span>{webinar.duration}</span>
                <span>•</span>
                <span>Published {new Date(webinar.published_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>

              <p className="text-gray-700 leading-relaxed mb-6">
                {webinar.description}
              </p>

              {webinar.key_takeaways && webinar.key_takeaways.length > 0 && (
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Key Takeaways</h3>
                  <ul className="space-y-2">
                    {webinar.key_takeaways.map((takeaway, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{takeaway}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1 lg:sticky lg:top-24 self-start">
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <img
                    src={webinar.speaker.image}
                    alt={webinar.speaker.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900">{webinar.speaker.name}</h3>
                    <p className="text-sm text-gray-600">{webinar.speaker.designation}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-700">{webinar.speaker.description}</p>
              </CardContent>
            </Card>

            {hasAccess && showProgramCTA && webinar.program && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardContent className="p-6">
                  <div className="mb-4">
                    <Badge className="bg-blue-600 text-white hover:bg-blue-700 mb-3">
                      Relevant Program
                    </Badge>
                    {webinar.university_image_url && (
                      <div className="my-3" data-testid="university-logo">
                        <img
                          src={webinar.university_image_url}
                          alt="University"
                          className="w-full h-auto rounded-md object-contain"
                        />
                      </div>
                    )}
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {webinar.program.name}
                    </h3>
                    <p className="text-sm text-gray-700">
                      {webinar.program.short_description}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={() => window.open(webinar.program.program_page_url, '_blank')}
                    >
                      View Program Details
                    </Button>
                    {webinar.program.web_view_brochure_url && (
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => window.open(webinar.program.web_view_brochure_url, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Download Brochure
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {!hasAccess && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Unlock Full Access
                  </h3>
                  <p className="text-sm text-gray-700 mb-4">
                    Submit your details to watch the complete webinar recording and discover relevant learning programs.
                  </p>
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => setIsLeadFormOpen(true)}
                  >
                    Get Access Now
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Related Webinars Section */}
      {relatedWebinars.length > 0 && (
        <section className="bg-gray-100 py-12">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Webinars</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedWebinars.map(related => {
                const relatedThumbnail = getWebinarThumbnail(related);
                return (
                  <Link 
                    key={related.webinar_id} 
                    to={`/webinar/${related.webinar_id}`}
                    className="group"
                    data-testid={`related-webinar-${related.webinar_id}`}
                  >
                    <Card className="overflow-hidden h-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={relatedThumbnail}
                          alt={related.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=450&fit=crop';
                          }}
                        />
                        <div className="absolute top-2 left-2">
                          <Badge className="bg-white/90 text-gray-900 text-xs">
                            {related.category}
                          </Badge>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 group-hover:text-blue-600 transition-colors">
                          {related.title}
                        </h3>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            <span>{related.speaker.name}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>{related.duration}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Footer />

      <LeadFormModal
        isOpen={isLeadFormOpen}
        onClose={() => setIsLeadFormOpen(false)}
        onSubmit={handleLeadFormSubmit}
        webinarTitle={webinar.title}
        webinarId={webinar.webinar_id}
      />
    </div>
  );
};

export default WebinarDetailPage;
